import Environment from './environment';

export function resolveApiFileUrl(filePath: string): string {
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }
  if (filePath.startsWith('/static/') || filePath.startsWith('/uploads/')) {
    return `${Environment.VITE_API_URL}${filePath}`;
  }
  const trimmed = filePath.startsWith('/') ? filePath.slice(1) : filePath;
  return `${Environment.VITE_API_URL}/${trimmed}`;
}

export function canPreviewInBrowser(fileName: string): boolean {
  return /\.(pdf|png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName);
}

export type AttachmentFileSource = {
  fileName: string;
  filePath?: string;
  localFile?: File;
};

export async function fetchRemoteFileBlob(
  filePath: string,
  accessToken: string | null,
): Promise<Blob> {
  const url = resolveApiFileUrl(filePath);
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch file (${response.status})`);
  }

  return response.blob();
}

export async function downloadRemoteFile(
  filePath: string,
  accessToken: string | null,
  fileName?: string,
): Promise<void> {
  const blob = await fetchRemoteFileBlob(filePath, accessToken);
  const blobUrl = URL.createObjectURL(blob);
  const name = fileName ?? filePath.split('/').pop() ?? 'file';

  try {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = name;
    link.click();
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

export async function downloadAttachment(
  source: AttachmentFileSource,
  accessToken: string | null,
): Promise<void> {
  if (source.localFile) {
    const blobUrl = URL.createObjectURL(source.localFile);
    try {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = source.fileName;
      link.click();
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
    return;
  }

  if (source.filePath) {
    await downloadRemoteFile(source.filePath, accessToken, source.fileName);
    return;
  }

  throw new Error('No file source');
}

export async function openRemoteFile(
  filePath: string,
  accessToken: string | null,
  fileName?: string,
): Promise<void> {
  const blob = await fetchRemoteFileBlob(filePath, accessToken);
  const blobUrl = URL.createObjectURL(blob);
  const name = fileName ?? filePath.split('/').pop() ?? 'file';

  try {
    if (canPreviewInBrowser(name)) {
      const preview = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!preview) {
        throw new Error('Popup blocked');
      }
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      return;
    }

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = name;
    link.click();
    URL.revokeObjectURL(blobUrl);
  } catch {
    URL.revokeObjectURL(blobUrl);
    throw new Error('Could not open file');
  }
}

export function attachmentSourceFromFields(
  fileName: string,
  filePath?: string | null,
  localFile?: File | null,
): AttachmentFileSource | null {
  if (localFile instanceof File) {
    return { fileName, localFile };
  }
  if (filePath) {
    return { fileName, filePath };
  }
  return null;
}
